import Swiper, { Navigation, Pagination } from 'swiper';

/**
 * Initializes the stepsA1 module, which sets up a Swiper instance
 * with navigation and pagination, including a custom step pagination.
 */
function stepsA1() {
    Swiper.use([Navigation, Pagination]);

    /**
     * Initializes the slider and related pagination components within the specified block element.
     *
     * @param {HTMLElement} block - The container block element that hosts the slider and its pagination elements. This element should contain necessary child elements for initializing the slider.
     * @return {void}
     */
    function init(block) {
        /**
         * Slide element
         * @type {HTMLElement}
         */
        const sliderContainer = block.querySelector('.swiper-container');
        if (!sliderContainer) {
            return;
        }

        /**
         * Index of the active slide by default.
         * @type {number}
         */
        const ACTIVE_INDEX = 0;

        /**
         * Steps pagination container element.
         * @type {HTMLElement}
         */
        const stepsPagination = block.querySelector('.js-pagination-steps');

        /**
         * Stores a reference to the progress bar element used for pagination within the specified block.
         * It is selected based on the CSS class 'js-pagination-progressbar'.
         *
         * @type {Element | null}
         */
        const progressPaginationWrapper = block.querySelector(
            '.js-pagination-progressbar-wrapper',
        );

        const swiper = new Swiper(sliderContainer, {
            slidesPerView: 1,
            spaceBetween: 0,
            autoHeight: true,
            initialSlide: ACTIVE_INDEX,
            pagination: {
                el: '.js-pagination-progressbar',
                type: 'progressbar',
            },
            navigation: {
                nextEl: sliderContainer.querySelector(
                    '.js-navigation .swiper-button-next',
                ),
                prevEl: sliderContainer.querySelector(
                    '.js-navigation .swiper-button-prev',
                ),
            },
            on: {
                init: initActions,
                slideChange: (slider) =>
                    updateStepsPagination(slider.activeIndex),
            },
        });

        /**
         * Initializes actions for step-based and progress-based pagination, enabling
         * interactivity such as navigating to specific slides and updating cursor styles.
         *
         * - For step-based pagination, it listens for click events on pagination buttons
         *   and navigates to the corresponding slide.
         * - For progress-based pagination, it updates the active slide based on clicks
         *   or mouse movements across the progress bar.
         *
         * @return {void} This method does not return a value.
         */
        function initActions() {
            if (stepsPagination) {
                updateStepsPagination(ACTIVE_INDEX);

                stepsPagination.addEventListener('click', (event) => {
                    const button = event.target.closest(
                        '.js-pag-step-btn[data-index]',
                    );
                    if (!button || !swiper) {
                        return;
                    }

                    const index = Number(button.dataset.index);
                    if (!Number.isInteger(index)) {
                        return;
                    }

                    event.preventDefault();
                    swiper.slideTo(index);
                });
            }

            if (progressPaginationWrapper) {
                /**
                 * Catches clicks on the progress bar element and updates the active slide index accordingly.
                 */
                progressPaginationWrapper.addEventListener('click', (event) => {
                    const wrapperRect =
                        progressPaginationWrapper.getBoundingClientRect();
                    const cursorX = event.clientX - wrapperRect.left;
                    const progress = Math.max(
                        0,
                        Math.min(1, cursorX / wrapperRect.width),
                    );

                    let slideIndex = getSlideIndexByProgress(progress);
                    if (slideIndex !== undefined) {
                        swiper.slideTo(slideIndex);
                    }
                });

                /**
                 * Catches mouse movements on the progress bar element and updates the cursor style accordingly.
                 */
                progressPaginationWrapper.addEventListener(
                    'mousemove',
                    (event) => {
                        const wrapperRect =
                            progressPaginationWrapper.getBoundingClientRect();
                        const cursorX = event.clientX - wrapperRect.left;
                        const progress = Math.max(
                            0,
                            Math.min(1, cursorX / wrapperRect.width),
                        );

                        let slideIndex = getSlideIndexByProgress(progress);
                        if (slideIndex === swiper.activeIndex) {
                            progressPaginationWrapper.style.cursor = 'default';
                            progressPaginationWrapper.title = '';
                        } else {
                            progressPaginationWrapper.style.cursor = 'pointer';
                            progressPaginationWrapper.title = `Go to slide ${
                                slideIndex + 1
                            }`;
                        }
                    },
                );
            }
        }

        /**
         * Updates the step pagination to reflect the active slide index.
         * @param {number} activeSlideIndex - The currently active slide index.
         */
        function updateStepsPagination(activeSlideIndex = ACTIVE_INDEX) {
            if (!stepsPagination) {
                return;
            }

            stepsPagination
                .querySelectorAll('.js-pag-step-item')
                .forEach((item, index) => {
                    if (index === 0) {
                        item.classList.add('animate');
                        setTimeout(() => {
                            item.classList.remove('animate');
                        }, 300);
                    }
                    item.classList.toggle('active', index === activeSlideIndex);
                });
        }

        /**
         * Returns a map of slide indices and their corresponding progress values.
         * @return {object}
         */
        function getSlidesProgressMap() {
            const slides = swiper && swiper.slides ? swiper.slides : [];
            if (slides.length < 1) {
                return [];
            }

            const step = 1 / slides.length;
            const map = {};
            let min, max;
            for (let i = 0; i < slides.length; i++) {
                min = i * step;
                max = i === slides.length ? 1 : (i + 1) * step;
                map[i] = {
                    min,
                    max,
                };
            }

            return map;
        }

        /**
         * Returns the slide index corresponding to the specified progress value.
         * @param progress
         * @return {number}
         */
        function getSlideIndexByProgress(progress) {
            const slidesProgressMap = getSlidesProgressMap();
            let slideIndex;
            for (const key in slidesProgressMap) {
                const { min, max } = slidesProgressMap[key];
                if (progress >= min && progress < max) {
                    slideIndex = Number(key);
                    break;
                }
            }

            return slideIndex;
        }
    }

    document.querySelectorAll("[data-module='stepsA1']").forEach(init);
}

export default stepsA1;
